import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./file-preview-modal.scss";
import PDFViewer from "../../../../../common/components/PDFViewer";

const getExtension = (fileName: string): string =>
  fileName?.split(".").pop()?.toLowerCase() || "";

const bytesToMB = (bytes?: number) =>
  bytes ? (bytes / (1024 * 1024)).toFixed(2) : "0";

const FilePreviewModal = ({
  open,
  onClose,
  fileUrl,
  fileName,
  id,
  previewLink,
  size,
}: {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  id: number;
  previewLink?: string;
  size?: number;
}) => {
  const [textContent, setTextContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  const encodedFileUrl = encodeURIComponent(fileUrl);
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedFileUrl}`;

  const ext = getExtension(fileName);
  const isImage = ["png", "jpg", "jpeg"].includes(ext);
  const isVideo = ext === "mp4";
  const isPDF = ext === "pdf";
  const isText = ["txt", "csv"].includes(ext);
  const isDocxSupported = ["doc", "docx"].includes(ext);
  const isMicrosoftOfficeFile = ["xlsx", "ppt", "pptx"].includes(ext);

  // File size limits
  const DOC_MAX_MB = 15;
  const XLSX_MAX_MB = 25;

  const fileSizeMB = parseFloat(bytesToMB(size));

  // Check restriction
  const exceedsLimit =
    (isDocxSupported && fileSizeMB > DOC_MAX_MB) ||
    (isMicrosoftOfficeFile && fileSizeMB > XLSX_MAX_MB) ||
    (isPDF && fileSizeMB > DOC_MAX_MB);

  // Determine message details
  const getLimitInfo = () => {
    if (isPDF) return { type: "Pdf", max: DOC_MAX_MB };
    if (isDocxSupported) return { type: "Word (.docx)", max: DOC_MAX_MB };
    if (isMicrosoftOfficeFile)
      return { type: "Office files (.xlsx, .ppt, .pptx)", max: XLSX_MAX_MB };
    return null;
  };

  const limitInfo = getLimitInfo();

  useEffect(() => {
    if (open) {
      setTextContent("");
      setLoading(false);
      setIframeLoading(false);

      if (isDocxSupported || isPDF || isMicrosoftOfficeFile) {
        setIframeLoading(true);
      }

      if (isText && fileUrl) {
        setLoading(true);
        fetch(fileUrl)
          .then((res) => res.text())
          .then((text) => setTextContent(text))
          .catch(() => setTextContent("Failed to load text content"))
          .finally(() => setLoading(false));
      }
    }
  }, [open, fileUrl, ext]);

  const getFileViewerUrl = () => {
    let googlePdfPreviewUrl;
    if (isPDF) {
      googlePdfPreviewUrl = `${previewLink || fileUrl}#toolbar=0&navpanes=0`;
    }
    // if (isPDF) return googlePdfPreviewUrl;
    if (isDocxSupported) return officeViewerUrl;
    if (isMicrosoftOfficeFile) return officeViewerUrl;
    return fileUrl;
  };

  return (
    <div className="file-view-wrapper">
      <Dialog open={open} onClose={onClose} className="file-view-popup">
        <DialogTitle>
          {fileName}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent key={id} className="file-preview-content">
          {/* Restriction Message */}
          {exceedsLimit && limitInfo ? (
            <div className="file-size-warning" style={{ padding: "20px" }}>
              <p>
                <strong>Preview not available:</strong>
              </p>
              <p>
                We can preview <b>{limitInfo.type}</b> files up to{" "}
                <b>{limitInfo.max} MB</b>.
              </p>
              <p>
                Your file size is <b>{fileSizeMB} MB</b>. Please download and
                view it manually.
              </p>
              <a
                href={fileUrl}
                download={fileName}
                className="download-link"
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  marginTop: "10px",
                  display: "inline-block",
                }}
              >
                Download File
              </a>
            </div>
          ) : (
            <>
              {isImage && (
                <div className="image-file-wrapper">
                  <img
                    src={fileUrl}
                    alt="Preview"
                    className="image-file"
                    key={id}
                  />
                </div>
              )}

              {isVideo && (
                <div className="video-file-wrapper">
                  <video controls className="video-file" key={id}>
                    <source src={fileUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              {isText && (
                <pre
                  style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                >
                  {loading ? (
                    <div className="file-loader-spinner">
                      <CircularProgress />
                    </div>
                  ) : (
                    textContent
                  )}
                </pre>
              )}

              {(isDocxSupported || isMicrosoftOfficeFile) && (
                <div className="iframe-container" style={{ position: "relative" }}>
                  {iframeLoading && (
                    <div className="file-loader-spinner">
                      <CircularProgress />
                    </div>
                  )}
                  <iframe
                    src={getFileViewerUrl()}
                    title="File Preview"
                    width="100%"
                    height="100%"
                    style={{ border: "none", minHeight: "500px" }}
                    onLoad={() => setIframeLoading(false)}
                  />
                </div>
              )}
              {isPDF && <PDFViewer fileUrl={previewLink || fileUrl || ""} />}
              {!isImage &&
                !isPDF &&
                !isVideo &&
                !isText &&
                !isDocxSupported &&
                !isMicrosoftOfficeFile && (
                  <p>Preview not supported for this file type.</p>
                )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FilePreviewModal;
